import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import FieldLabel from "../../components/FieldLabel";
import type { Sprint } from "../../../../core/data/redux/projects/types";

interface CompleteSprintModalProps {
  open: boolean;
  sprint: Sprint | null;
  unfinishedCount: number;
  plannedSprints: Sprint[];
  onSubmit: (target: "BACKLOG" | number) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

const CompleteSprintModal: React.FC<CompleteSprintModalProps> = ({ open, sprint, unfinishedCount, plannedSprints, onSubmit, onCancel, submitting = false }) => {
  const [target, setTarget] = useState<"BACKLOG" | number>("BACKLOG");

  useEffect(() => { if (open) setTarget("BACKLOG"); }, [open]);

  const options = useMemo(() => [
    { value: "BACKLOG", label: "Backlog" },
    ...plannedSprints.filter((s) => s.id !== sprint?.id).map((s) => ({ value: s.id, label: s.name })),
  ], [plannedSprints, sprint?.id]);

  return (
    <Modal open={open} onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: {
          padding: 0,
          borderRadius: 12,
          overflow: "hidden",
        },
        header: {
          padding: "14px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          padding: "16px 24px 20px",
        },
        footer: {
          padding: "10px 20px 14px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      title={<div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ width: 30, height: 30, borderRadius: 8, background: "#f59e0b18", display: "flex", alignItems: "center", justifyContent: "center", color: "#f59e0b", fontSize: 15 }}><CheckCircleOutlined /></div><div><div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>Complete Sprint</div><div style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 400 }}>{sprint?.name || "Sprint"}</div></div></div>}
      footer={<div style={{ padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}><button className="btn btn-light border px-4" style={{ borderRadius: 8 }} onClick={onCancel} disabled={submitting}>Cancel</button><button className="btn px-4" style={{ borderRadius: 8, background: "#f59e0b", color: "#fff", fontWeight: 600 }} onClick={() => onSubmit(target)} disabled={submitting}>{submitting ? <><span className="spinner-border spinner-border-sm me-2" />Completing...</> : <><CheckCircleOutlined className="me-2" />Complete Sprint</>}</button></div>}
    >
      <div className="d-flex flex-column gap-3">
        {unfinishedCount === 0 ? (
          <div style={{ padding: "12px 16px", background: "#ecfdf5", borderRadius: 8, color: "#059669", fontSize: 13 }}>
            <i className="ti ti-check me-2" />All issues are done. Completing this sprint will mark it as COMPLETED.
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 16px", background: "#fffbeb", borderRadius: 8, color: "#d97706", fontSize: 13 }}>
              <i className="ti ti-alert-triangle me-2" /><strong>{unfinishedCount}</strong> unfinished {unfinishedCount === 1 ? "issue" : "issues"} found. Where should they go?
            </div>
            <div>
              <FieldLabel>Move unfinished issues to</FieldLabel>
              <Select size="large" style={{ width: "100%" }} value={target as any} onChange={(v) => setTarget(v as any)} options={options as any} />
              {target !== "BACKLOG" && (<span className="text-muted small mt-1 d-block">Issues will be moved into the selected planned sprint.</span>)}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CompleteSprintModal;
